// The value is `<pageLoadId>-<seq>`: a prefix search on the page load id returns every
// request that page made, the full value pins down a single request.

export const REQUEST_ID_HEADER = 'X-Request-Id'

// Kept on `window` so every Folloze bundle sharing a page (this app and client-sdk based
// bundles) reports the same page load id and one sequence.
const STATE_KEY = '__flzRequestIdState'

// Folloze services this app calls cross-origin. Anything else - enrichment vendors, S3,
// the CDN the assets come from - must not get the header: it would turn a simple request
// into a preflighted one against a service that never allowed the header.
const FOLLOZE_ENDPOINT_KEYS = ['ANALYTICS_SERVICE_ADDRESS', 'CONTENT_URL']

function randomId() {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : undefined

  if (cryptoObj && cryptoObj.randomUUID) {
    return cryptoObj.randomUUID()
  }
  if (cryptoObj && cryptoObj.getRandomValues) {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`
}

function state() {
  const host = typeof window !== 'undefined' ? window : globalThis

  if (!host[STATE_KEY]) {
    host[STATE_KEY] = { pageLoadId: randomId(), sequence: 0 }
  }
  return host[STATE_KEY]
}

export function pageLoadId() {
  return state().pageLoadId
}

export function nextRequestId() {
  const current = state()
  current.sequence += 1

  return `${current.pageLoadId}-${current.sequence}`
}

function originOf(url) {
  try {
    return new URL(url, window.location.href).origin
  } catch (e) {
    return null
  }
}

function follozeOrigins() {
  const envConfig = (window.FollozeState && window.FollozeState.envConfig) || {}

  return FOLLOZE_ENDPOINT_KEYS
    .map(key => envConfig[key] && originOf(envConfig[key]))
    .filter(Boolean)
    .concat(window.location.origin)
}

export function isFollozeUrl(url) {
  if (!url) {
    return true // relative to the current page, so same origin
  }

  const origin = originOf(url)

  return !!origin && follozeOrigins().indexOf(origin) !== -1
}

function targetUrl(config) {
  const url = config.url || ''

  if (!config.baseURL || /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) {
    return url
  }
  return `${config.baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`
}

// Request interceptor for every axios instance the app uses, and for Angular's $http -
// both hand the interceptor a config carrying `url` and `headers`.
export function withRequestId(config) {
  if (isFollozeUrl(targetUrl(config))) {
    config.headers = config.headers || {}
    config.headers[REQUEST_ID_HEADER] = nextRequestId()
  }
  return config
}
